import { useTranslations } from '@/i18n';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from './button';
import { Spinner } from '@/components/ui/shadcn-io/spinner';

interface ConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmButtonVariant?: 'danger' | 'primary' | 'secondary' | 'brand';
  isLoading?: boolean;
  errorMessage?: string;
}

/**
 * Reusable confirmation dialog component that wraps shadcn's Dialog
 * Provides consistent styling matching Figma design for all confirmation modals
 *
 * @example
 * <ConfirmationDialog
 *   isOpen={showDeleteConfirm}
 *   onClose={() => setShowDeleteConfirm(false)}
 *   onConfirm={handleDelete}
 *   title={t('deleteConfirmation.title')}
 *   message={t('deleteConfirmation.message')}
 *   confirmText={t('deleteConfirmation.confirm')}
 *   confirmButtonVariant="danger"
 * />
 */
export default function ConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  cancelText,
  confirmButtonVariant = 'danger',
  isLoading = false,
  errorMessage,
}: ConfirmationDialogProps) {
  const tCommon = useTranslations('common');

  // Use provided text or fallback to common translations
  const confirmButtonText = confirmText || tCommon('confirm');
  const cancelButtonText = cancelText || tCommon('cancel');

  const getConfirmButtonClasses = () => {
    switch (confirmButtonVariant) {
      case 'danger':
        return 'bg-[#e5484d] text-white hover:bg-[#dc3d42] disabled:opacity-50';
      case 'primary':
        return 'bg-[#242424] text-white hover:bg-[#1a1a1a] disabled:opacity-50';
      case 'secondary':
        return 'bg-[#f1f1f1] text-black hover:bg-[#e4e4e4] disabled:opacity-50';
      case 'brand':
        return 'text-white hover:opacity-90 disabled:opacity-50';
      default:
        return 'bg-[#e5484d] text-white hover:bg-[#dc3d42] disabled:opacity-50';
    }
  };

  const getConfirmButtonStyle = () => {
    if (confirmButtonVariant === 'brand') {
      return {
        background: 'linear-gradient(90deg, #F1656A -66.86%, #ED2891 47.89%, #42419A 167.14%)',
      };
    }
    return {};
  };

  const handleAttemptClose = () => {
    if (isLoading) {
      return;
    }
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleAttemptClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-[25rem] gap-0 rounded-3xl border-0 p-0"
        showCloseButton={false}
      >
        {/* Hidden DialogTitle for accessibility */}
        <DialogTitle className="sr-only">{title}</DialogTitle>

        {/* Header - Matches Figma design exactly */}
        <div className="flex items-center justify-between px-6 py-4">
          <h2 className="text-[18px] leading-7 font-bold text-black">{title}</h2>
          <Button
            onClick={handleAttemptClose}
            disabled={isLoading}
            variant="unstyled"
            size="none"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f1f1f1] transition-colors hover:bg-[#e4e4e4] disabled:opacity-50"
            aria-label="Close dialog"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Button>
        </div>

        {/* Message Content */}
        <div className="px-6 pb-4">
          <p className="text-base leading-6 text-black">{message}</p>
          {errorMessage && (
            <div className="mt-3 rounded-2xl border border-[#ffecec] bg-[#fff6f6] px-4 py-3 text-sm leading-5 text-[#e5484d]">
              {errorMessage}
            </div>
          )}
        </div>

        {/* Action Buttons - Matches Figma design */}
        <div className="flex items-center justify-end gap-2 px-6 pb-6">
          <Button
            type="button"
            variant="ghost"
            onClick={handleAttemptClose}
            disabled={isLoading}
            className="h-auto rounded-xl px-3.5 py-2.5 text-sm font-medium text-black hover:bg-[#f9f9f9] disabled:opacity-50"
          >
            {cancelButtonText}
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className={`h-auto rounded-full px-4 py-3 text-[15px] font-medium transition-colors ${getConfirmButtonClasses()}`}
            style={getConfirmButtonStyle()}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <Spinner variant="circle" className="h-4 w-4" />
                <span>{tCommon('processing')}</span>
              </div>
            ) : (
              confirmButtonText
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
